/**
 * Presentational row inside the all-pipelines drawer (SE-5489). Pure JSX —
 * receives the resolved card-count label + the already-bound onPress from its
 * Container.
 *
 * Each row shows the four AC-mandated facets: the board name, a meta line of
 * "type - owner/assignee - last activity", and a trailing card-count badge.
 * @module features/player-kanban/components/molecules/SeeAllPipelinesDrawer/PipelineDrawerRow/PipelineDrawerRowView
 */

import { memo } from "react";

import type { PipelineDrawerRow } from "@/features/player-kanban/hooks/useSeeAllPipelinesData";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_SEE_ALL_PIPELINES_DRAWER } from "e2e/testId";

/** Props for the row view. */
export interface PipelineDrawerRowViewProps {
  /** Row data to render. */
  readonly row: PipelineDrawerRow;
  /** Resolved card-count label (e.g. "12" or "100+"). */
  readonly countLabel: string;
  /** Formatted last-activity label. */
  readonly lastActivityLabel: string;
  /** Press handler (already bound to the row id by the Container). */
  readonly onPress: () => void;
}

/**
 * Pipeline drawer row view.
 * @param root0 - Component props
 * @param root0.row - Row data to render
 * @param root0.countLabel - Resolved card-count label
 * @param root0.lastActivityLabel - Formatted last-activity label
 * @param root0.onPress - Press handler
 */
const PipelineDrawerRowView = ({
  row,
  countLabel,
  lastActivityLabel,
  onPress,
}: PipelineDrawerRowViewProps) => (
  <Pressable
    testID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.rowFor(row.id)}
    onPress={onPress}
    className="web:hover:bg-surface-raised min-h-12 flex-row items-center justify-between gap-2 overflow-hidden rounded-lg px-6 py-2"
  >
    <Stack className="min-w-0 flex-1 gap-px">
      <Text
        variant="unstyled"
        className="web:truncate min-w-0 text-title-sm text-content-secondary"
        numberOfLines={1}
      >
        {row.name}
      </Text>
      <Text
        variant="caption"
        testID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.rowMetaFor(row.id)}
        className="web:truncate min-w-0 text-content-muted"
        numberOfLines={1}
      >
        {row.typeLabel} · {row.ownerLabel} · {lastActivityLabel}
      </Text>
    </Stack>
    {/* Neutral badge only. The spec draws ONE row with a reddish fill
        (#fef1f1); nothing in the spec or our data says what earns it, so
        guessing a trigger would put a false alert on real pipelines. Flagged
        for the designer rather than approximated. */}
    <Box
      testID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.rowCountFor(row.id)}
      className="shrink-0 rounded-sm bg-surface-raised px-2 py-1"
    >
      <Text variant="caption" className="text-content-primary">
        {countLabel} players
      </Text>
    </Box>
  </Pressable>
);

PipelineDrawerRowView.displayName = "PipelineDrawerRowView";

export default memo(PipelineDrawerRowView);
